/*
================================================================================
  lumi-assessment-engine.js — Story 4.11
  Schema-driven multi-program assessment engine.

  Reads ALL clinical rules from DOM data-* attributes set by the Liquid section.
  Zero hardcoded clinical knowledge. Changing a rule = changing a dropdown in
  Theme Editor.

  Session keys (per-program namespace as of Story 4.13):
    lumi_ae_draft_<programId>          — in-progress assessment state
    lumi_ae_pending_<programId>        — post-submit relay payload (eligible/clearance only)
    lumi_ae_lead_fired_<programId>     — Lead pixel dedup
    lumi_ae_relay_failed_<programId>   — Worker relay failure flag

  Migration shim at init time copies legacy un-namespaced keys (e.g.
  `lumi_ae_draft`) into the LUMislim namespace (`lumi_ae_draft_lumislim`)
  exactly once, protecting patients mid-assessment at deploy time.

  Globals exposed for inline onclick handlers:
    window.lumiAEBack, window.lumiAENext, window.lumiAESubmit, window.lumiAESetStep
================================================================================
*/

(function () {
  'use strict';

  /* ── Module state ── */
  var AE_CONFIG = null;        /* populated by lumiAEInit() from wrapper data-* */
  var AE_KEYS = null;          /* per-program sessionStorage keys; built in lumiAEInit */
  var AE_CURRENT = 1;
  var AE_RESTORING = false;    /* blocks lumiAESaveDraft during restore */
  var AE_DRAFT_TIMER;          /* debounce handle */
  var AE_ORDER_REF = '';       /* generated at submit */
  var AE_INITIALISED = false;

  /* ── Tiny helpers ── */
  function $id(id)  { return document.getElementById(id); }
  function $q(sel)  { return document.querySelector(sel); }
  function $qa(sel) { return document.querySelectorAll(sel); }

  function lumiAEScrollTop() {
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
  }

  function lumiAEFirePixel(eventName, params) {
    try {
      if (typeof window.fbq !== 'undefined') {
        window.fbq('track', eventName, params || {});
      }
    } catch (e) { /* silent */ }
  }

  function lumiAEShowFieldErr(id, show) {
    var el = $id(id);
    if (!el) return;
    el.classList[show ? 'add' : 'remove']('lumi-visible');
  }

  function lumiAEMarkInput(id, invalid) {
    var el = $id(id);
    if (!el) return;
    el.classList[invalid ? 'add' : 'remove']('lumi-input-error');
  }

  /* ── Init: read all data-* config from wrapper ── */
  function lumiAEInit() {
    var wrapper = $id('lumi-ae-wrapper');
    if (!wrapper) return;

    AE_CONFIG = {
      total:         parseInt(wrapper.dataset.aeTotal, 10) || 5,
      names:         (wrapper.dataset.aeNames || '').split('|'),
      bmiGate:       wrapper.dataset.aeBmiGate === 'true',
      bmiThreshold:  parseFloat(wrapper.dataset.aeBmiThreshold) || 27.5,
      workerUrl:     wrapper.dataset.aeWorker || '',
      relayToken:    wrapper.dataset.aeToken || '',
      programId:     wrapper.dataset.aeProgram || 'lumislim',
      programLabel:  wrapper.dataset.aeProgramLabel || '',
      eligibleUrl:   wrapper.dataset.aeEligibleUrl || '/pages/lumislim-eligible',
      pixelCategory: wrapper.dataset.aePixelCategory || 'Telehealth Assessment'
    };

    /* Build per-program sessionStorage keys AFTER AE_CONFIG.programId is
       populated. Module-load construction would yield `lumi_ae_draft_undefined`. */
    AE_KEYS = {
      draft:       'lumi_ae_draft_'        + AE_CONFIG.programId,
      pending:     'lumi_ae_pending_'      + AE_CONFIG.programId,
      leadFired:   'lumi_ae_lead_fired_'   + AE_CONFIG.programId,
      relayFailed: 'lumi_ae_relay_failed_' + AE_CONFIG.programId
    };

    /* Migration shim — protect live LUMislim patients mid-assessment at deploy
       time. Copies legacy un-namespaced keys (e.g. lumi_ae_draft) into the
       lumislim-scoped keys (lumi_ae_draft_lumislim) exactly once.

       Scoped to programId === 'lumislim' only — LUMislim is the only program
       that had pre-4.13 traffic with un-namespaced keys. Without this scope,
       a returning LUMislim patient hitting /pages/assessment-lumitimeless would
       have their LUMislim draft (BMI/contra answers) silently copied into the
       LUMitimeless namespace, contaminating the new program's restore. */
    if (AE_CONFIG.programId === 'lumislim') {
      ['draft', 'pending', 'leadFired', 'relayFailed'].forEach(function (k) {
        var newKey = AE_KEYS[k];
        var oldKey = newKey.replace('_lumislim', '');
        try {
          var oldVal = sessionStorage.getItem(oldKey);
          if (oldVal && !sessionStorage.getItem(newKey)) {
            sessionStorage.setItem(newKey, oldVal);
            sessionStorage.removeItem(oldKey);
          }
        } catch (e) {}
      });
    }

    /* Always re-wire: shopify:section:load replaces the wrapper element, so
       previously-attached listeners are now on a detached DOM node and
       silently do nothing. Re-binding to the current wrapper is idempotent
       — the old wrapper is GC'd with its listeners attached.
       Only the draft restore is guarded (banner injection is non-idempotent
       and the user has already seen / dismissed it on first load). */
    lumiAEWireEvents();
    if (!AE_INITIALISED) {
      lumiAERestoreDraft();
      AE_INITIALISED = true;
    }

    if (window.location.search.indexOf('assessment_preview=true') !== -1) {
      lumiAEInitDebugOverlay();
    }
  }

  /* ── Step navigation ── */
  function lumiAESetStep(n) {
    clearTimeout(AE_DRAFT_TIMER);
    var currEl = $id('lumi-ae-step-' + AE_CURRENT);
    if (currEl) currEl.classList.remove('lumi-active');
    AE_CURRENT = n;
    var nextEl = $id('lumi-ae-step-' + n);
    if (nextEl) nextEl.classList.add('lumi-active');

    var pct = Math.round((n / AE_CONFIG.total) * 100);
    var fill = $id('lumi-ae-progress-fill');
    if (fill) fill.style.width = pct + '%';
    var bar = $id('lumi-ae-progress-bar');
    if (bar) bar.setAttribute('aria-valuenow', n);

    var counter = $id('lumi-ae-step-counter');
    if (counter) counter.textContent = 'Step ' + n + ' of ' + AE_CONFIG.total;
    var nameEl = $id('lumi-ae-step-name');
    if (nameEl) nameEl.textContent = AE_CONFIG.names[n - 1] || '';

    lumiAEScrollTop();
    lumiAESaveDraft();
  }
  window.lumiAESetStep = lumiAESetStep;

  function lumiAEBack() {
    if (AE_CURRENT > 1) lumiAESetStep(AE_CURRENT - 1);
  }
  window.lumiAEBack = lumiAEBack;

  /* ── Validate one step ── */
  function lumiAEValidateStep(n) {
    var wrapper = $id('lumi-ae-wrapper');
    if (!wrapper) return true;
    var valid = true;

    /* Block-driven question groups */
    var groups = wrapper.querySelectorAll(
      '.lumi-ae-group[data-ae-step="' + n + '"]'
    );
    groups.forEach(function (g) {
      var required = g.dataset.aeRequired === 'true';
      if (!required) return;
      var name = g.dataset.aeGroup;
      var hasSelection = wrapper.querySelector(
        '[data-group="' + name + '"].lumi-selected'
      );
      var errEl = $id('lumi-ae-err-' + name);
      if (!hasSelection) {
        valid = false;
        if (errEl) errEl.classList.add('lumi-visible');
      } else {
        if (errEl) errEl.classList.remove('lumi-visible');
      }
    });

    /* Block-driven number inputs */
    var numInputs = wrapper.querySelectorAll(
      '.lumi-ae-number-input[data-ae-step="' + n + '"]'
    );
    numInputs.forEach(function (block) {
      var required = block.dataset.aeRequired === 'true';
      var inputId = block.dataset.aeInputId;
      var min = parseFloat(block.dataset.aeMin);
      var max = parseFloat(block.dataset.aeMax);
      var input = $id(inputId);
      if (!input) return;
      var raw = input.value.trim();
      var num = parseFloat(raw);
      var bad = false;
      if (required && !raw) bad = true;
      if (raw && (isNaN(num) || (!isNaN(min) && num < min) || (!isNaN(max) && num > max))) bad = true;
      lumiAEMarkInput(inputId, bad);
      var errEl = $id('lumi-ae-err-' + inputId);
      if (errEl) errEl.classList[bad ? 'add' : 'remove']('lumi-visible');
      if (bad) valid = false;
    });

    /* Block-driven text inputs */
    var textInputs = wrapper.querySelectorAll(
      '.lumi-ae-text-input[data-ae-step="' + n + '"]'
    );
    textInputs.forEach(function (block) {
      var required = block.dataset.aeRequired === 'true';
      var inputId = block.dataset.aeInputId;
      var input = $id(inputId);
      if (!input) return;
      /* Skip validation if conditionally hidden via show_if_group */
      var showIf = block.dataset.aeShowIfGroup;
      if (showIf) {
        var hasParent = wrapper.querySelector('[data-group="' + showIf + '"].lumi-selected');
        if (!hasParent) return;
      }
      var bad = required && !input.value.trim();
      lumiAEMarkInput(inputId, bad);
      var errEl = $id('lumi-ae-err-' + inputId);
      if (errEl) errEl.classList[bad ? 'add' : 'remove']('lumi-visible');
      if (bad) valid = false;
    });

    return valid;
  }

  /* ── Validate every step 1..total. On first failure, jump the user to the
     failing step so the error is visible. Called on Submit to defend against
     DevTools / sessionStorage tampering that drops a user on the final step
     with empty prior steps (clinical-gate bypass). ── */
  function lumiAEValidateAllSteps() {
    if (!AE_CONFIG) return false;
    for (var i = 1; i <= AE_CONFIG.total; i++) {
      if (!lumiAEValidateStep(i)) {
        if (AE_CURRENT !== i) lumiAESetStep(i);
        return false;
      }
    }
    return true;
  }

  /* ── Continue button: validate, scan ALL prior steps for disqualifies ── */
  function lumiAENext() {
    if (!lumiAEValidateStep(AE_CURRENT)) return;

    /* Check ALL selected buttons (steps 1..current) for disqualifies rule.
       Prevents bypass: patient picks under-18 on Step 1, hits Back, then
       advances through Step 2 without re-triggering the disq check. */
    var disqBtn = $q(
      '#lumi-ae-wrapper .lumi-opt-btn.lumi-selected[data-clinical-rule="disqualifies"]'
    );
    if (disqBtn) {
      try { sessionStorage.removeItem(AE_KEYS.draft); } catch (e) {}
      lumiAEShowDisqScreen(disqBtn);
      return;
    }

    if (AE_CURRENT < AE_CONFIG.total) lumiAESetStep(AE_CURRENT + 1);
  }
  window.lumiAENext = lumiAENext;

  /* ── Compute result: reads DOM, applies BMI gate ── */
  function lumiAEComputeResult() {
    var selected = $qa('#lumi-ae-wrapper .lumi-opt-btn.lumi-selected');
    var hasHardStop = false;
    var hasClearance = false;
    var hiddenProducts = [];

    selected.forEach(function (btn) {
      var rule = btn.dataset.clinicalRule || 'none';
      if (rule === 'hard_stop') hasHardStop = true;
      if (rule === 'clearance') hasClearance = true;
      var hides = btn.dataset.hidesProducts;
      if (hides) {
        hides.split(',').forEach(function (p) {
          var t = p.trim();
          if (t && hiddenProducts.indexOf(t) === -1) hiddenProducts.push(t);
        });
      }
    });

    window.lumiAEHiddenProducts = hiddenProducts;

    if (hasHardStop) return 'not-eligible';

    /* BMI gate.
       Forces clearance when BMI < threshold. Also fails SAFE (→ clearance)
       when the gate is enabled but BMI cannot be computed (missing or
       invalid inputs, blocks removed from a step the patient never reached,
       DevTools tampering). Mirrors legacy lumitalk-assessment2.liquid
       behaviour: "cannot prove eligible → require physician review". */
    if (AE_CONFIG && AE_CONFIG.bmiGate && !hasClearance) {
      var wEl = $q('#lumi-ae-wrapper [data-bmi-role="weight"]');
      var hEl = $q('#lumi-ae-wrapper [data-bmi-role="height"]');
      var bmiComputable = false;
      if (wEl && hEl) {
        var w = parseFloat(wEl.value);
        var hCm = parseFloat(hEl.value);
        if (w > 0 && hCm > 0) {
          bmiComputable = true;
          var bmi = w / Math.pow(hCm / 100, 2);
          if (bmi < AE_CONFIG.bmiThreshold) hasClearance = true;
        }
      }
      if (!bmiComputable) hasClearance = true;
    }

    return hasClearance ? 'clearance' : 'eligible';
  }

  /* ── BMI live calc ── */
  function lumiAECalcBMI() {
    var wEl = $q('#lumi-ae-wrapper [data-bmi-role="weight"]');
    var hEl = $q('#lumi-ae-wrapper [data-bmi-role="height"]');
    var card = $id('lumi-ae-bmi-card');
    if (!card || !wEl || !hEl) return;

    var w = parseFloat(wEl.value);
    var hCm = parseFloat(hEl.value);
    if (!w || !hCm || hCm <= 0) {
      card.classList.remove('lumi-visible');
      return;
    }

    var bmi = Math.round((w / Math.pow(hCm / 100, 2)) * 10) / 10;
    var valEl = $id('lumi-ae-bmi-val');
    if (valEl) valEl.textContent = bmi.toFixed(1);

    var cat, note;
    if (bmi < 18.5) {
      cat = 'Underweight';
      note = 'Our doctor will assess your full profile during consultation.';
    } else if (bmi < 25) {
      cat = 'Normal weight';
      note = 'Our doctor will review your specific health goals during your free consultation.';
    } else if (bmi < 27) {
      cat = 'Slightly above normal';
      note = 'You may qualify. Our doctor will confirm suitability during your consultation.';
    } else if (bmi < 30) {
      cat = 'Overweight — you are a candidate';
      note = 'You are a strong candidate. A doctor will confirm during consultation.';
    } else {
      cat = 'Obese — strong candidate';
      note = 'You are a strong candidate. Our doctors commonly prescribe for patients at this BMI.';
    }
    var catEl = $id('lumi-ae-bmi-cat');
    var noteEl = $id('lumi-ae-bmi-note');
    if (catEl) catEl.textContent = cat;
    if (noteEl) noteEl.textContent = note;
    card.classList.add('lumi-visible');
  }
  window.lumiAECalcBMI = lumiAECalcBMI;

  /* ── Disq screen (fired by disqualifies rule at lumiAENext) ── */
  function lumiAEShowDisqScreen(disqBtn) {
    var form = $id('lumi-ae-main-form');
    if (form) form.style.display = 'none';
    var screen = $id('lumi-ae-disq-screen');
    if (!screen) return;

    /* Customise messaging based on which option triggered the disq (data-disq-title / data-disq-text on the option block).
       Falls back to defaults set in the section settings. */
    var titleEl = $id('lumi-ae-disq-title');
    var textEl = $id('lumi-ae-disq-text');
    if (disqBtn) {
      var customTitle = disqBtn.dataset.disqTitle;
      var customText = disqBtn.dataset.disqText;
      if (customTitle && titleEl) titleEl.textContent = customTitle;
      if (customText && textEl) textEl.textContent = customText;
    }
    screen.classList.add('lumi-active');
    lumiAEScrollTop();
  }

  /* ── Result screens (post-submit routing) ── */
  function lumiAEShowResultScreen(result) {
    if (result === 'not-eligible') {
      var refEl = $id('lumi-ae-not-eligible-ref');
      if (refEl) refEl.textContent = AE_ORDER_REF;
      var ne = $id('lumi-ae-not-eligible-screen');
      if (ne) ne.classList.add('lumi-active');
      lumiAEScrollTop();
      return;
    }
    if (result === 'clearance') {
      var refEl2 = $id('lumi-ae-clearance-ref');
      if (refEl2) refEl2.textContent = AE_ORDER_REF;
      var cl = $id('lumi-ae-clearance-screen');
      if (cl) cl.classList.add('lumi-active');
      lumiAEScrollTop();
      return;
    }
    /* eligible → redirect with ?ref= + optional ?hide= */
    var hiddenProds = window.lumiAEHiddenProducts || [];
    var url = AE_CONFIG.eligibleUrl + '?ref=' + encodeURIComponent(AE_ORDER_REF);
    if (hiddenProds.length) url += '&hide=' + encodeURIComponent(hiddenProds.join(','));
    window.location.href = url;
  }
  window.lumiAERouteEligibility = lumiAEShowResultScreen;

  /* ── Submit ── */
  function lumiAESubmit() {
    var wrapper = $id('lumi-ae-wrapper');
    if (!wrapper) return;

    /* 1. Validate ALL steps — not just the current one. Defends against
       tampered drafts / DevTools that skip a user past required clinical
       questions onto Step 5. If a prior step fails, the user is jumped
       back to that step so the error is visible. */
    if (!lumiAEValidateAllSteps()) return;

    /* Re-run the disqualifies scan after multi-step validation too —
       restoration may have re-added a disqualifying selection that the
       patient never reached via the normal Continue button. */
    var disqAtSubmit = $q(
      '#lumi-ae-wrapper .lumi-opt-btn.lumi-selected[data-clinical-rule="disqualifies"]'
    );
    if (disqAtSubmit) {
      try { sessionStorage.removeItem(AE_KEYS.draft); } catch (e) {}
      lumiAEShowDisqScreen(disqAtSubmit);
      return;
    }

    /* 2. Validate hardcoded contact fields (always on last step) */
    var fullnameEl = $id('lumi-ae-fullname');
    var viberEl    = $id('lumi-ae-viber');
    var emailEl    = $id('lumi-ae-email');
    var c1El       = $id('lumi-ae-c1');
    var c2El       = $id('lumi-ae-c2');

    var fullname = fullnameEl ? fullnameEl.value.trim() : '';
    var viber    = viberEl    ? viberEl.value.trim()    : '';
    var email    = emailEl    ? emailEl.value.trim()    : '';
    var phRe     = /^(\+?63|0)9\d{9}$/;
    var emailRe  = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    var valid    = true;

    if (!fullname) { lumiAEShowFieldErr('lumi-ae-err-fullname', true);  lumiAEMarkInput('lumi-ae-fullname', true);  valid = false; }
    else           { lumiAEShowFieldErr('lumi-ae-err-fullname', false); lumiAEMarkInput('lumi-ae-fullname', false); }

    var viberClean = viber.replace(/[\s\-]/g, '');
    if (!phRe.test(viberClean)) { lumiAEShowFieldErr('lumi-ae-err-viber', true);  lumiAEMarkInput('lumi-ae-viber', true);  valid = false; }
    else                        { lumiAEShowFieldErr('lumi-ae-err-viber', false); lumiAEMarkInput('lumi-ae-viber', false); }

    if (email && !emailRe.test(email)) { lumiAEShowFieldErr('lumi-ae-err-email', true);  lumiAEMarkInput('lumi-ae-email', true);  valid = false; }
    else                               { lumiAEShowFieldErr('lumi-ae-err-email', false); lumiAEMarkInput('lumi-ae-email', false); }

    var allChecked = !!(c1El && c1El.checked && c2El && c2El.checked);
    if (!allChecked) { lumiAEShowFieldErr('lumi-ae-err-consent', true);  valid = false; }
    else             { lumiAEShowFieldErr('lumi-ae-err-consent', false); }

    if (!valid) return;

    /* 3. Disable button to prevent double-submit */
    var btn = $id('lumi-ae-submit-btn');
    var origLabel = btn ? btn.innerHTML : '';
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = '<span class="lumi-spinner"></span> Submitting...';
    }

    /* 4. Generate order ref */
    AE_ORDER_REF = 'LT-' + new Date().toISOString().slice(0, 10).replace(/-/g, '') +
                   '-' + (Math.floor(Math.random() * 9000) + 1000);

    /* 5. Compute result (single source of truth) */
    var resultScreen = lumiAEComputeResult();

    /* 6. Build assessment body — dynamic from selected buttons + inputs */
    var body = lumiAEBuildBody(fullname, email, viber, resultScreen);

    /* 7. Build relay payload — defense-in-depth data minimization for not-eligible */
    var relayPayload = (resultScreen === 'not-eligible')
      ? {
          orderRef: AE_ORDER_REF,
          submittedAt: new Date().toISOString(),
          resultScreen: resultScreen,
          programId: AE_CONFIG.programId
        }
      : {
          orderRef: AE_ORDER_REF,
          submittedAt: new Date().toISOString(),
          resultScreen: resultScreen,
          programId: AE_CONFIG.programId,
          patientName: fullname,
          email: email || '',
          viber: viber,
          body: body
        };

    /* 8. SessionStorage recovery hatch — eligible/clearance only */
    if (resultScreen !== 'not-eligible') {
      try {
        sessionStorage.setItem(AE_KEYS.pending, JSON.stringify(relayPayload));
      } catch (e) {}
    }

    /* 9. Fire-and-forget Worker POST.
       Clear the stale failure flag at the start of each attempt so the flag
       reflects THIS submission's outcome, not a past one. */
    try { sessionStorage.removeItem(AE_KEYS.relayFailed); } catch (e) {}
    if (AE_CONFIG.workerUrl) {
      try {
        fetch(AE_CONFIG.workerUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Lumi-Token': AE_CONFIG.relayToken
          },
          body: JSON.stringify(relayPayload)
        }).then(function (resp) {
          if (!resp || !resp.ok) {
            try { sessionStorage.setItem(AE_KEYS.relayFailed, '1'); } catch (e) {}
          }
        }).catch(function () {
          try { sessionStorage.setItem(AE_KEYS.relayFailed, '1'); } catch (e) {}
        });
      } catch (e) {
        try { sessionStorage.setItem(AE_KEYS.relayFailed, '1'); } catch (e2) {}
      }
    }

    /* 10. Fire Lead pixel (dedup via sessionStorage) — all paths */
    if (!sessionStorage.getItem(AE_KEYS.leadFired)) {
      lumiAEFirePixel('Lead', {
        content_name: 'Lumitalk Assessment Completed',
        content_category: AE_CONFIG.pixelCategory,
        value: 0,
        currency: 'PHP'
      });
      try { sessionStorage.setItem(AE_KEYS.leadFired, '1'); } catch (e) {}
    }

    /* 11. Show loading screen, clear draft, route after 4.5s */
    var form = $id('lumi-ae-main-form');
    if (form) form.style.display = 'none';
    var loadingEl = $id('lumi-ae-loading-screen');
    if (loadingEl) loadingEl.classList.add('lumi-active');
    try { sessionStorage.removeItem(AE_KEYS.draft); } catch (e) {}
    lumiAEScrollTop();

    setTimeout(function () {
      if (loadingEl) loadingEl.classList.remove('lumi-active');
      lumiAEShowResultScreen(resultScreen);
      if (btn) { btn.disabled = false; btn.innerHTML = origLabel; }
    }, 4500);
  }
  window.lumiAESubmit = lumiAESubmit;

  /* ── Build assessment body for the email relay ──
     Walks STEPS in order, then BLOCKS within each step in DOM order. Each
     step produces a section header (`STEP N · {STEP_NAME}`) for clinical
     scanability — matches the legacy section's hand-grouped format but
     fully driven by section settings + block_order. No hardcoded knowledge
     of which blocks live in which section.

     Adapts automatically when Lumi adds/removes steps, renames steps,
     reorders blocks, or adds new question_groups / inputs in Theme Editor.
  ── */
  function lumiAEBuildBody(fullname, email, viber, resultScreen) {
    var wrapper = $id('lumi-ae-wrapper');
    var lines = [
      '=== LUMITALK MEDICAL ASSESSMENT ===',
      'ORDER REFERENCE: ' + AE_ORDER_REF,
      'PROGRAM: ' + (AE_CONFIG.programLabel || AE_CONFIG.programId || '—'),
      'RESULT: ' + resultScreen,
      '',
      'CONTACT DETAILS',
      'Full name: ' + fullname,
      'Email: ' + (email || 'Not provided'),
      'Viber: ' + viber
    ];

    if (!wrapper || !AE_CONFIG) {
      lines.push('', '=== END OF ASSESSMENT ===');
      return lines.join('\n');
    }

    for (var step = 1; step <= AE_CONFIG.total; step++) {
      var stepEl = $id('lumi-ae-step-' + step);
      if (!stepEl) continue;

      var rawStepName = (AE_CONFIG.names[step - 1] || '').trim();
      var stepHeader = rawStepName
        ? ('STEP ' + step + ' · ' + rawStepName.toUpperCase())
        : ('STEP ' + step);

      var stepLines = [];

      /* Walk every block-rendered element inside this step in DOM order so
         the email mirrors the visual flow the patient just completed
         (e.g. weight + height + goal_weight live in BODY PROFILE, not at
         the bottom dumped after lifestyle). */
      stepEl.querySelectorAll('.lumi-ae-group, .lumi-ae-number-input, .lumi-ae-text-input').forEach(function (block) {
        if (block.classList.contains('lumi-ae-group')) {
          var qLabel = block.dataset.aeLabel || block.dataset.aeGroup;
          var name = block.dataset.aeGroup;
          var selected = wrapper.querySelectorAll('[data-group="' + name + '"].lumi-selected');
          var vals = [];
          selected.forEach(function (b) {
            vals.push(b.dataset.label || b.textContent.trim().replace(/\s*✓\s*$/, ''));
          });
          stepLines.push(qLabel + ': ' + (vals.join(', ') || '—'));

        } else if (block.classList.contains('lumi-ae-number-input')) {
          var nLabel = block.dataset.aeLabel || block.dataset.aeInputId;
          var unit = block.dataset.aeUnit || '';
          var inp = $id(block.dataset.aeInputId);
          var nVal = inp ? (inp.value || '—') : '—';
          stepLines.push(nLabel + ': ' + nVal + (unit && nVal !== '—' ? ' ' + unit : ''));

        } else if (block.classList.contains('lumi-ae-text-input')) {
          /* Skip text inputs that are hidden because their show_if_group
             parent has no selection — even if a value persists from a
             prior interaction, the conditional context no longer applies. */
          var showIf = block.dataset.aeShowIfGroup;
          if (showIf) {
            var parentSelected = wrapper.querySelector('[data-group="' + showIf + '"].lumi-selected');
            if (!parentSelected) return;
          }
          var tLabel = block.dataset.aeLabel || block.dataset.aeInputId;
          var tInp = $id(block.dataset.aeInputId);
          var tVal = tInp ? tInp.value.trim() : '';
          if (tVal) stepLines.push(tLabel + ': ' + tVal);
        }
      });

      if (stepLines.length) {
        lines.push('', stepHeader);
        Array.prototype.push.apply(lines, stepLines);
      }
    }

    lines.push('', '=== END OF ASSESSMENT ===');
    return lines.join('\n');
  }

  /* ── Draft save ── */
  function lumiAESaveDraft() {
    if (AE_RESTORING) return;
    try {
      var wrapper = $id('lumi-ae-wrapper');
      if (!wrapper) return;

      var draft = { step: AE_CURRENT, groups: {}, inputs: {} };

      /* Collect all unique group names from buttons in the wrapper */
      var seenGroups = {};
      wrapper.querySelectorAll('[data-group]').forEach(function (btn) {
        seenGroups[btn.dataset.group] = true;
      });
      Object.keys(seenGroups).forEach(function (g) {
        var sel = wrapper.querySelectorAll('[data-group="' + g + '"].lumi-selected');
        draft.groups[g] = Array.prototype.map.call(sel, function (b) { return b.dataset.val; });
      });

      /* Collect all input + textarea values inside the wrapper */
      wrapper.querySelectorAll('input[id], textarea[id]').forEach(function (el) {
        if (el.type === 'checkbox') draft.inputs[el.id] = !!el.checked;
        else draft.inputs[el.id] = el.value;
      });

      sessionStorage.setItem(AE_KEYS.draft, JSON.stringify(draft));
    } catch (e) {}
  }

  /* ── Draft restore ── */
  function lumiAERestoreDraft() {
    var wrapper = $id('lumi-ae-wrapper');
    if (!wrapper) return;
    try {
      var raw = sessionStorage.getItem(AE_KEYS.draft);
      if (!raw) return;
      var d = JSON.parse(raw);
      AE_RESTORING = true;

      /* Restore button selections — capped by the group's selection-type
         and max_selections so a tampered draft can't put a single-select
         group into a multi state or exceed a max-N. */
      if (d.groups) {
        Object.keys(d.groups).forEach(function (g) {
          var groupEl = wrapper.querySelector('.lumi-ae-group[data-ae-group="' + g + '"]');
          var selType = groupEl ? (groupEl.dataset.aeSelection || 'single') : 'single';
          var maxSel  = groupEl ? (parseInt(groupEl.dataset.aeMax, 10) || 0) : 0;
          var vals    = d.groups[g] || [];
          if (selType === 'single') vals = vals.slice(0, 1);
          else if (maxSel > 0)      vals = vals.slice(0, maxSel);
          vals.forEach(function (v) {
            var btn = wrapper.querySelector('[data-group="' + g + '"][data-val="' + v + '"]');
            if (btn) btn.classList.add('lumi-selected');
          });
        });
      }

      /* Restore input values */
      if (d.inputs) {
        Object.keys(d.inputs).forEach(function (id) {
          var el = $id(id);
          if (!el) return;
          if (el.type === 'checkbox') el.checked = !!d.inputs[id];
          else el.value = d.inputs[id];
        });
      }

      lumiAECalcBMI();

      /* Partial-step drop-back: if previous step has no required data, drop back one */
      var target = (typeof d.step === 'number' && d.step >= 1 && d.step <= AE_CONFIG.total) ? d.step : 1;
      if (target > 1) {
        /* Coarse heuristic: scan prior step for ANY selection or non-empty input */
        var hasAny = !!wrapper.querySelector('[data-group].lumi-selected');
        if (!hasAny) target = 1;
      }
      lumiAESetStep(target);

      /* Resume banner */
      var existing = $id('lumi-ae-resume-banner');
      if (!existing) {
        var banner = document.createElement('div');
        banner.id = 'lumi-ae-resume-banner';
        banner.setAttribute('role', 'status');
        banner.style.cssText = 'background:#F0EAF8;border-left:3px solid #6B4C8A;padding:10px 14px;border-radius:4px;margin-bottom:16px;font-size:0.95rem;color:#2B1B3D;font-family:sans-serif;';
        banner.textContent = 'Your progress was saved — you are on Step ' + target + ' of ' + AE_CONFIG.total + '.';
        var stepEl = $id('lumi-ae-step-' + target);
        if (stepEl) stepEl.insertBefore(banner, stepEl.firstChild);

        var dismiss = function () {
          var b = $id('lumi-ae-resume-banner');
          if (b) b.remove();
          document.removeEventListener('click', dismiss, true);
          document.removeEventListener('input', dismiss, true);
          document.removeEventListener('change', dismiss, true);
        };
        document.addEventListener('click', dismiss, true);
        document.addEventListener('input', dismiss, true);
        document.addEventListener('change', dismiss, true);
      }
    } catch (e) {
      /* malformed JSON or sessionStorage unavailable — silent, form loads at Step 1 */
    } finally {
      AE_RESTORING = false;
    }
  }

  /* ── Event wiring (delegated) ── */
  function lumiAEWireEvents() {
    var wrapper = $id('lumi-ae-wrapper');
    if (!wrapper) return;

    /* Delegated click for option buttons */
    wrapper.addEventListener('click', function (e) {
      var btn = e.target.closest('.lumi-opt-btn');
      if (!btn) return;
      var group = btn.dataset.group;
      var groupEl = wrapper.querySelector('.lumi-ae-group[data-ae-group="' + group + '"]');
      if (!groupEl) return;
      var selType = groupEl.dataset.aeSelection || 'single';
      var maxSel  = parseInt(groupEl.dataset.aeMax, 10) || 0;
      var val     = btn.dataset.val;
      var allInGroup = wrapper.querySelectorAll('[data-group="' + group + '"]');

      if (selType === 'single') {
        allInGroup.forEach(function (b) { b.classList.remove('lumi-selected'); });
        btn.classList.add('lumi-selected');
      } else {
        /* Multi-select: "none"-style values deselect everything else */
        if (val === 'none' || val === 'nothing') {
          allInGroup.forEach(function (b) { b.classList.remove('lumi-selected'); });
          btn.classList.add('lumi-selected');
        } else {
          /* Deselect any none/nothing option */
          var noneBtn = wrapper.querySelector(
            '[data-group="' + group + '"][data-val="none"], [data-group="' + group + '"][data-val="nothing"]'
          );
          if (noneBtn) noneBtn.classList.remove('lumi-selected');

          if (btn.classList.contains('lumi-selected')) {
            btn.classList.remove('lumi-selected');
          } else {
            var currentCount = wrapper.querySelectorAll(
              '[data-group="' + group + '"].lumi-selected'
            ).length;
            if (maxSel > 0 && currentCount >= maxSel) return;
            btn.classList.add('lumi-selected');
          }
        }
      }

      /* Hide group error on selection */
      var errEl = $id('lumi-ae-err-' + group);
      if (errEl) errEl.classList.remove('lumi-visible');

      /* show_if_group: reveal conditional text inputs */
      wrapper.querySelectorAll('.lumi-ae-text-input[data-ae-show-if-group="' + group + '"]').forEach(function (el) {
        var hasSelection = wrapper.querySelector('[data-group="' + group + '"].lumi-selected');
        el.style.display = hasSelection ? '' : 'none';
      });

      lumiAESaveDraft();
    });

    /* Debounced save on text/number/textarea input */
    wrapper.addEventListener('input', function (e) {
      var t = e.target;
      if (t.matches && t.matches('input[type="number"], input[type="text"], input[type="tel"], input[type="email"], textarea')) {
        clearTimeout(AE_DRAFT_TIMER);
        AE_DRAFT_TIMER = setTimeout(lumiAESaveDraft, 500);
        if (t.dataset && (t.dataset.bmiRole === 'weight' || t.dataset.bmiRole === 'height')) {
          lumiAECalcBMI();
        }
        /* Clear inline error on input */
        lumiAEMarkInput(t.id, false);
        var ie = $id('lumi-ae-err-' + t.id);
        if (ie) ie.classList.remove('lumi-visible');
      }
    });

    /* Checkbox change → immediate save */
    wrapper.addEventListener('change', function (e) {
      if (e.target && e.target.type === 'checkbox') {
        lumiAESaveDraft();
        /* Clear consent error if both checked */
        var c1 = $id('lumi-ae-c1');
        var c2 = $id('lumi-ae-c2');
        if (c1 && c2 && c1.checked && c2.checked) {
          var ce = $id('lumi-ae-err-consent');
          if (ce) ce.classList.remove('lumi-visible');
        }
      }
    });

    /* Initial show_if_group hide state */
    wrapper.querySelectorAll('.lumi-ae-text-input[data-ae-show-if-group]').forEach(function (el) {
      var g = el.dataset.aeShowIfGroup;
      var hasSelection = wrapper.querySelector('[data-group="' + g + '"].lumi-selected');
      if (!hasSelection) el.style.display = 'none';
    });
  }

  /* ── Debug overlay (assessment_preview=true) ── */
  function lumiAEInitDebugOverlay() {
    if ($id('lumi-ae-debug-overlay')) return;
    var overlay = document.createElement('div');
    overlay.id = 'lumi-ae-debug-overlay';
    overlay.style.cssText = 'position:fixed;bottom:12px;right:12px;width:340px;max-height:60vh;overflow:auto;background:rgba(0,0,0,0.88);color:#fff;font-family:Menlo,monospace;font-size:11px;line-height:1.45;padding:12px 14px;border-radius:8px;z-index:9999;box-shadow:0 8px 24px rgba(0,0,0,0.3);';
    document.body.appendChild(overlay);

    function render() {
      var wrapper = $id('lumi-ae-wrapper');
      if (!wrapper) return;
      var selected = wrapper.querySelectorAll('.lumi-opt-btn.lumi-selected');
      var rules = { disqualifies: [], hard_stop: [], clearance: [], none: [] };
      selected.forEach(function (b) {
        var rule = b.dataset.clinicalRule || 'none';
        if (!rules[rule]) rules[rule] = [];
        rules[rule].push(b.dataset.group + '=' + b.dataset.val);
      });
      var wEl = wrapper.querySelector('[data-bmi-role="weight"]');
      var hEl = wrapper.querySelector('[data-bmi-role="height"]');
      var bmi = '—';
      if (wEl && hEl && wEl.value && hEl.value) {
        var w = parseFloat(wEl.value), h = parseFloat(hEl.value) / 100;
        if (w && h) bmi = (w / (h * h)).toFixed(1);
      }
      var result = '—';
      try { result = lumiAEComputeResult(); } catch (e) {}

      overlay.innerHTML =
        '<strong style="color:#F28DB8">⚕ LUMI ASSESSMENT DEBUG</strong><br>' +
        '<span style="color:#9AE6B4">step:</span> ' + AE_CURRENT + ' / ' + (AE_CONFIG ? AE_CONFIG.total : '?') + '<br>' +
        '<span style="color:#9AE6B4">program:</span> ' + (AE_CONFIG ? AE_CONFIG.programId : '?') + '<br>' +
        '<span style="color:#9AE6B4">BMI:</span> ' + bmi + ' (threshold: ' + (AE_CONFIG ? AE_CONFIG.bmiThreshold : '?') + ', gate: ' + (AE_CONFIG && AE_CONFIG.bmiGate ? 'on' : 'off') + ')<br>' +
        '<span style="color:#F56565">disqualifies:</span> ' + (rules.disqualifies.join(', ') || '—') + '<br>' +
        '<span style="color:#F6AD55">hard_stop:</span> ' + (rules.hard_stop.join(', ') || '—') + '<br>' +
        '<span style="color:#ECC94B">clearance:</span> ' + (rules.clearance.join(', ') || '—') + '<br>' +
        '<span style="color:#9AE6B4">none:</span> ' + (rules.none.length) + ' selected<br>' +
        '<span style="color:#F28DB8">→ computed result:</span> <strong>' + result + '</strong><br>' +
        '<span style="color:#9AE6B4">hidden products:</span> ' + ((window.lumiAEHiddenProducts || []).join(', ') || '—');
    }
    render();
    setInterval(render, 500);
  }

  /* ── Bootstrap ── */
  document.addEventListener('DOMContentLoaded', lumiAEInit);
  document.addEventListener('shopify:section:load', function (e) {
    if (e && e.detail && e.detail.sectionId) {
      /* Re-run init only — wire-events guarded by AE_INITIALISED */
      lumiAEInit();
    }
  });

}());
